import { alpha, Box, Divider, Grid, Modal, Stack, Typography } from '@mui/material';
import { Link as NavLink, useNavigate } from '@tanstack/react-router';
import ArrowRightIcon from 'assets/icons/right-icon.svg?react';
import theme from 'providers/theme/theme';
import { memo, MouseEvent } from 'react';
import { useAnalytics } from 'services/analytics';
import { useLocalStorageStore, useStore } from 'store';
import { LUMI_ONLY_SUPPORT_EMAIL, LUMI_PLACE_DOMAIN, SUPPORT_EMAIL } from 'utils';
import { useShallow } from 'zustand/shallow';

import { profileDialogData } from './utils/data';

export const ProfileDialog = memo(() => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const { isDrawerDialogOpen } = useStore(
    useShallow((state) => ({
      isDrawerDialogOpen: state.isDrawerDialogOpen,
    })),
  );

  const { domain } = useLocalStorageStore(
    useShallow((state) => ({
      domain: state.domain,
    })),
  );

  const handleClose = (_: MouseEvent<HTMLElement>, screenName: string): void => {
    if (screenName) {
      trackEvent('account_button_tap', {
        button: screenName.toLowerCase(),
      });
    }

    useStore.getState().setIsDrawerDialogOpen(false);
  };

  const handleLogOut = (): void => {
    useLocalStorageStore.getState().logout();
    void navigate({ to: '/sign-in' });
  };

  return (
    <Modal
      keepMounted
      disableScrollLock
      open={isDrawerDialogOpen}
      onClose={handleClose}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          bottom: 24,
          left: 220,
          bgcolor: 'common.white',
          p: 6,
          borderRadius: 2.5,
          boxShadow: `0px 0px 10px 0px ${alpha(theme.palette.common.black, 0.1)}`,
        }}
      >
        <Box minWidth={290} pt={2}>
          <Stack>
            {profileDialogData.map(({ title, href, isRedirected = false, isNativeLink = false, isLogOut, Icon }, i) => {
              const isLastItem = i === profileDialogData.length - 1;

              return (
                <Grid
                  key={title}
                  container
                  component={isRedirected ? NavLink : isNativeLink ? 'a' : 'div'}
                  to={isRedirected ? href : null}
                  href={
                    isNativeLink
                      ? `mailto:${domain === LUMI_PLACE_DOMAIN ? LUMI_ONLY_SUPPORT_EMAIL : SUPPORT_EMAIL}`
                      : null
                  }
                  onClick={isLogOut ? handleLogOut : (e: MouseEvent<HTMLElement>): void => handleClose(e, title)}
                  sx={{
                    cursor: 'pointer',
                    mb: isLastItem ? 0 : 3,
                    '&:hover, &:focus-visible': {
                      '& .MuiGrid-root ': {
                        color: 'primary.main',
                      },
                    },
                  }}
                >
                  <Grid sx={{ color: 'custom.textGrey' }}>
                    <Icon />
                  </Grid>
                  <Grid sx={{ flexGrow: 1, pl: 4 }}>
                    <Typography variant="body1" mb={isLastItem ? 0 : 3}>
                      {title}
                    </Typography>
                    {!isLastItem && <Divider />}
                  </Grid>
                  <Grid sx={{ color: 'custom.textGrey' }}>
                    <ArrowRightIcon />
                  </Grid>
                </Grid>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
});
